/**
 * SuccessView — real post-conversion screen (redesign pass B, item 14).
 *
 * Replaces the old "Redirecting to home in 10 seconds…" countdown card.
 * Shows the submission reference, a recap of what was sent (box theme,
 * item count, quantity, contact email), the 24-hour promise addressed to
 * the customer's own email, and a concrete next step. No auto-redirect —
 * the customer leaves via the explicit "Back to home" link.
 */

import { motion } from 'motion/react';
import { Check, CalendarClock, ChevronLeft } from 'lucide-react';
import type { Cart, CustomerInfo } from '../types';
import { SiteFooter } from './SiteFooter';

interface Props {
  cart: Cart;
  customerInfo: CustomerInfo;
  /** Pipeline-returned id when available, else the client-side BB-XXXXXX. */
  reference: string | null;
  onReturnHome: () => void;
}

export function SuccessView({ cart, customerInfo, reference, onReturnHome }: Props) {
  const email = customerInfo.email || '';
  const itemCount =
    (cart.box.packagingId ? 1 : 0) + cart.box.merchIds.length + cart.box.finalTouchIds.length;
  // PLACEHOLDER: swap for the real booking URL (e.g. a Calendly link) when
  // marketing supplies one; until then the promotions mailbox is the next step.
  const bookCallHref = `mailto:promotions@ddg-europe.com?subject=${encodeURIComponent(
    `Intro call — quote ${reference ?? ''}`.trim(),
  )}`;

  const recap: { label: string; value: string }[] = [
    ...(cart.box.boxName ? [{ label: 'Box theme', value: cart.box.boxName }] : []),
    { label: 'Items in the box', value: `${itemCount} ${itemCount === 1 ? 'item' : 'items'}` },
    { label: 'Quantity', value: `${cart.qty} boxes` },
    ...(email ? [{ label: 'Contact email', value: email }] : []),
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white relative overflow-hidden">
      {/* Item 17 — the success screen keeps the second (toned-down)
          gradient-sphere moment. */}
      <div className="sphere-gradient w-[120vw] h-[120vw] max-w-[600px] max-h-[600px] -top-40 -right-[10%] opacity-40" />
      <div className="sphere-gradient w-[100vw] h-[100vw] max-w-[400px] max-h-[400px] -bottom-20 -left-[10%] opacity-30" />

      <div className="flex-1 flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="max-w-2xl w-full bg-white/80 backdrop-blur-xl rounded-[48px] shadow-2xl p-8 sm:p-12 border border-white/20 relative z-10"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 15 }}
          className="w-20 h-20 bg-linear-to-br from-[#57c858] to-[#356d4e] rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg shadow-[#356d4e]/30"
        >
          <Check className="w-10 h-10 text-white" />
        </motion.div>

        <h2 className="text-4xl sm:text-5xl font-black mb-3 text-[#444444] tracking-tight uppercase text-center">
          Request sent
          <span className="inline-block w-3 h-3 rounded-full bg-[#57c858] ml-2" />
        </h2>

        {reference && (
          <p className="text-center mb-8">
            <span className="inline-flex items-baseline gap-2 px-5 py-2 rounded-full bg-[#57c858]/10 border border-[#57c858]/30">
              <span className="text-[11px] font-black uppercase tracking-[0.2em] text-[#356d4e]/70">
                Reference
              </span>
              <span
                data-testid="success-reference"
                className="text-base font-black tracking-widest text-[#356d4e]"
              >
                {reference}
              </span>
            </span>
          </p>
        )}

        <div className="bg-gray-50 border border-gray-100 rounded-2xl divide-y divide-gray-100 mb-8">
          {recap.map((row) => (
            <div key={row.label} className="flex items-center justify-between gap-4 px-6 py-3.5">
              <span className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                {row.label}
              </span>
              <span className="text-sm font-bold text-[#444444] text-right truncate">
                {row.value}
              </span>
            </div>
          ))}
        </div>

        {/* Chapter 10 (F9) — "We'll email <address> within 24 hours…" was
            factually right (it interpolated the customer's own submitted
            address) but never said whose address it was: a tester who had
            submitted DDG's own promotions mailbox read it back as us emailing
            ourselves. Address the reader directly instead, and keep both facts
            — a confirmation goes out now, the proposal follows within 24 hours.
            When no address was captured there is nothing to confirm TO, so the
            first clause is dropped rather than made vague. */}
        <p
          data-testid="success-confirmation-line"
          className="text-base text-[#444444]/80 font-medium leading-relaxed text-center mb-10 max-w-md mx-auto"
        >
          {email ? (
            <>
              Confirmation on its way to you at{' '}
              <span className="font-bold text-[#356d4e]">{email}</span>. Your personalized proposal
              follows within 24 hours.
            </>
          ) : (
            <>Your personalized proposal follows within 24 hours.</>
          )}
        </p>

        <div className="flex flex-col items-center gap-5">
          <a
            href={bookCallHref}
            className="inline-flex items-center gap-2 px-8 py-4 bg-linear-to-r from-[#356d4e] to-[#57c858] text-white rounded-full font-black text-sm uppercase tracking-widest hover:scale-[1.03] transition-transform shadow-xl shadow-[#356d4e]/20"
          >
            <CalendarClock className="w-4 h-4" />
            Book an intro call
          </a>
          <button
            onClick={onReturnHome}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-[#444444]/70 uppercase tracking-widest hover:text-[#356d4e] hover:underline underline-offset-4 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to home
          </button>
        </div>
      </motion.div>
      </div>

      {/* Redesign pass C (item 16) — shared on-brand footer. */}
      <SiteFooter />
    </div>
  );
}
